/* eslint-disable array-callback-return */
import React, { useState, useRef, useContext, useEffect } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Grid,
    Hidden,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';
import { MoreVert } from '@mui/icons-material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';
import { COLORS } from '../../utils/Application_Constants';
import TimesheetStatusEnum, {
    TimesheetStatusEnumApi,
} from '../../components/ContentComponents/Timesheets/Status.enum';
import { config as ClientTableConfig } from '../../Client/Configurations/TimesheetReviewConfig';
import ToolTipComponent from '../../components/GlobalComponents/ToolTipComponent';
import StatusErrorDialog from './StatusErrorDialog';
import {
    updateTimesheetReview,
    getEmailRecipients,
    sendManualEmailRequest,
    getTimesheets,
    downloadTimesheet,
} from '../Services/TimesheetsService';
import RejectionDialog from './RejectionDialog';
import EmailDialog from './EmailDialog';
import DidNotWorkDialog from './DidNotWorkDialog';
import StatusWarningDialog from './StatusWarningDialog';
import { saveTimesheet, getTimesheetById } from '../../services/TimeAndPayService';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import {
    TimesheetUserGroupEnum,
    TimesheetUserGroupProdEnum,
} from '../../Admin/Configurations/OktaUserGroup.enum';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import SalesforceBookingLink from './SalesforceBookingLink';

const styles = (theme) => ({
    PaperRoot: {
        padding: '1.5rem 2rem',
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
    },
    tableScrollContainer: {
        width: '100%',
        overflowX: 'scroll',
        // Hide native horizontal scrollbar; sticky one is rendered below.
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE/Edge
        '&::-webkit-scrollbar': {
            display: 'none', // Chrome/Safari
        },
    },
    tableContent: {
        minWidth: '100%',
    },
    stickyHorizontalScrollbar: {
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        position: 'sticky',
        bottom: 0,
        left: 0,
        zIndex: 10,
        backgroundColor: 'white',
        paddingBottom: '2px',
        '&::-webkit-scrollbar': {
            height: '10px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: COLORS.LT_MIDNIGHT5,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.LT_MIDNIGHT25,
            borderRadius: '6px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: COLORS.LT_MIDNIGHT50,
        },
    },
    headerRow: {
        display: 'flex',
        borderRadius: '8px',
        flexWrap: 'nowrap',
        minWidth: '100%',
        '& > .MuiGrid-item': {
            flexShrink: 0,
            backgroundColor: COLORS.LT_MIDNIGHTBG1,
        },
        '& > .MuiGrid-item:first-child': {
            borderTopLeftRadius: '8px',
        },
        '& > .MuiGrid-item:last-child': {
            borderTopRightRadius: '8px',
            borderRight: `1px solid ${COLORS.LT_MIDNIGHT5}`,
        },
    },
    headerCell: {
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        '&:first-child': {
            paddingLeft: '24px',
        },
        '&:last-child': {
            paddingRight: '24px',
        },
    },
    paragraph3: {
        display: 'flex',
        alignItems: 'center',
        color: COLORS.LT_MIDNIGHT50,
        fontSize: '12px',
        lineHeight: '18px',
        '& span:hover': {
            cursor: 'pointer',
        },
    },
    menuButton: {
        color: COLORS.LT_MIDNIGHT25,
    },
    menuIcon: {
        color: COLORS.LT_MIDNIGHT25,
        display: 'flex',
        alignItems: 'center',
    },
    chipRoot: {
        textAlign: 'center',
        width: '100%',

        [theme.breakpoints.up('lg')]: {
            minWidth: '175px',
        },
        [theme.breakpoints.between(1280, 1530)]: {
            minWidth: '135px',
            marginRight: '8px',
        },
    },
    chipSubmitted: {
        backgroundColor: COLORS.GLITTER,
        color: COLORS.RICH_ELECTRIC_BLUE,
        borderColor: COLORS.RICH_ELECTRIC_BLUE,
    },
    chipReSubmitted: {
        backgroundColor: COLORS.GLITTER,
        color: COLORS.RICH_ELECTRIC_BLUE,
        borderColor: COLORS.RICH_ELECTRIC_BLUE,
    },
    chipApproved: {
        backgroundColor: COLORS.AQUA_SPRING,
        color: COLORS.KELLY_GREEN,
        borderColor: COLORS.KELLY_GREEN,
    },
    chipReApproved: {
        backgroundColor: COLORS.AQUA_SPRING,
        color: COLORS.KELLY_GREEN,
        borderColor: COLORS.KELLY_GREEN,
    },
    chipRejected: {
        backgroundColor: COLORS.LT_PEPPER10,
        color: COLORS.LT_PEPPER,
        borderColor: COLORS.LT_PEPPER,
    },
    chipPaid: {
        color: 'white',
        background: COLORS.LT_EMERALD,
    },
    tableRow: {
        flexWrap: 'nowrap',
        minWidth: '100%',
        '& > .MuiGrid-item': {
            flexShrink: 0,
            borderBottom: `1px solid ${COLORS.LT_MIDNIGHT5}`,
        },
        '& > .MuiGrid-item:first-child': {
            borderLeft: `1px solid ${COLORS.LT_MIDNIGHT5}`,
        },
        '& > .MuiGrid-item:last-child': {
            borderRight: `1px solid ${COLORS.LT_MIDNIGHT5}`,
        },
        '&:last-child > .MuiGrid-item:first-child': {
            borderBottomLeftRadius: '8px',
        },
        '&:last-child > .MuiGrid-item:last-child': {
            borderBottomRightRadius: '8px',
        },
        '&:hover > .MuiGrid-item': {
            transition: '.25s',
            cursor: 'pointer',
            background: COLORS.LT_MIDNIGHTBG1,
        },
    },
    tableCell: {
        padding: '16px',
        paddingTop: '24px',
        '&:first-child': {
            paddingLeft: '24px',
        },
        '& .MuiTypography-body2': {
            maxWidth: '100%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            textWrap: 'nowrap',
        },
    },
    commentsCell: {
        padding: '16px',
        paddingTop: '24px',
        '& .MuiTypography-body2': {
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            maxWidth: '100%',
            overflow: 'hidden',
        },
    },
    sortIcon: {
        transition: '.15s',
        '&.desc': {
            transform: 'rotate(180deg)',
        },
    },
    sortIcons: {
        '& $sortIcon': {
            margin: '-5px 0 -5px 4px',
        },
    },
    actionsCell: {
        padding: '4px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        [theme.breakpoints.between(1280, 1550)]: {
            padding: '8px 12px',
        },
    },
    filterList: {
        minWidth: '250px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    filterButton: {
        borderRadius: '20px',
        border: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        background: 'white',
        color: COLORS.LT_MIDNIGHT75,
        '&:hover': {
            background: COLORS.LT_JOURNEY10,
            color: COLORS.LT_JOURNEY,
            borderColor: COLORS.LT_JOURNEY,
        },
    },
    timesheetName: {
        '&.MuiTypography-body2': {
            maxWidth: 'calc(100% - 22px)',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            textWrap: 'nowrap',
        },
    },
    bookingName: {
        fontStyle: 'italic',
        color: COLORS.LT_MIDNIGHT50,
    },
    chatButtonIcon: {
        fontSize: '14px',
        color: COLORS.LT_MIDNIGHT25,
    },
    commentIcon: {
        textAlign: 'center',
        alignContent: 'center',
    },
    commentHeaderIcon: {
        textAlign: 'center',
        alignContent: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 16px',
        display: 'flex',
    },
    emptyText: {
        color: COLORS.LT_MIDNIGHT25,
    },
});

const TimesheetReviewCard = (props) => {
    const {
        classes,
        timesheets,
        weekEnding,
        sortBy,
        sortDirection,
        handleSort,
        isSorting,
        getData,
        setAlert,
        user,
        config,
    } = props;

    const [anchorEl, setAnchorEl] = useState(null);
    const tableScrollRef = useRef(null);
    const stickyScrollbarRef = useRef(null);
    const [stickyScrollWidth, setStickyScrollWidth] = useState('100%');
    const [statusErrorDialogOpen, setStatusErrorDialogOpen] = useState(false);
    const [recallErrorDialogOpen, setRecallErrorDialogOpen] = useState(false);
    const [recallErrorType, setRecallErrorType] = useState();
    const [recallDialogOpen, setRecallDialogOpen] = useState(false);
    const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [didNotWorkDialogOpen, setDidNotWorkDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [statusErrorType, setStatusErrorType] = useState('updated');
    const [requestedAction, setRequestedAction] = useState();
    const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
    const [selectedTimesheetReview, setSelectedTimesheetReview] = useState(null);
    const [rejectionReason, setRejectionReason] = useState(null);
    const [emailsList, setEmailsList] = useState([]);
    const [dnwTimesheetsList, setDnwTimesheetsList] = useState([]);
    const [dnwTimesheet, setDnwTimesheet] = useState([]);
    const isAdminMode = useRef(false);
    const history = useHistory();
    const flags = useFlags();
    const completedStatuses = [
        TimesheetStatusEnumApi.Approved,
        TimesheetStatusEnumApi.Rejected,
        TimesheetStatusEnumApi.Paid,
    ];
    const adminContext = useContext(AdminAuthContext);
    const isLTAssociateUser = adminContext?.isLTAssociateUser || false;
    const isProd = process.env.REACT_APP_EXECUTION_ENVIRONMENT === 'PROD';
    const { ltoeApiTimesheetEnableEditApprovedTimesheet } = flags;
    let userIsReadOnly = true;
    let isTSAdmin = false;
    if (isProd) {
        isTSAdmin =
            user?.groups?.some((group) =>
                group.includes(TimesheetUserGroupProdEnum.SystemAdministratorPROD)
            ) ||
            user?.groups?.some((group) =>
                group.includes(TimesheetUserGroupProdEnum.TimesheetAdminPROD)
            );
    } else {
        isTSAdmin =
            user?.groups?.some((group) =>
                group.includes(TimesheetUserGroupEnum.SystemAdministrator)
            ) ||
            user?.groups?.some((group) => group.includes(TimesheetUserGroupEnum.TimesheetAdmin));
    }
    isAdminMode.current = isTSAdmin;

    if (isTSAdmin || (user && !user.groups)) {
        userIsReadOnly = false;
    }

    const syncStickyScrollbarWidth = () => {
        const horizontalContent = tableScrollRef.current;
        if (!horizontalContent) return;
        setStickyScrollWidth(`${horizontalContent.scrollWidth}px`);
    };

    useEffect(() => {
        const horizontalContent = tableScrollRef.current;
        const stickyScrollbar = stickyScrollbarRef.current;
        if (!horizontalContent || !stickyScrollbar) return;

        syncStickyScrollbarWidth();

        let isSyncingFromTable = false;
        let isSyncingFromSticky = false;

        const onTableScroll = () => {
            if (isSyncingFromSticky) return;
            isSyncingFromTable = true;
            stickyScrollbar.scrollLeft = horizontalContent.scrollLeft;
            isSyncingFromTable = false;
        };

        const onStickyScroll = () => {
            if (isSyncingFromTable) return;
            isSyncingFromSticky = true;
            horizontalContent.scrollLeft = stickyScrollbar.scrollLeft;
            isSyncingFromSticky = false;
        };

        const onResize = () => syncStickyScrollbarWidth();

        horizontalContent.addEventListener('scroll', onTableScroll, { passive: true });
        stickyScrollbar.addEventListener('scroll', onStickyScroll, { passive: true });
        window.addEventListener('resize', onResize);

        return () => {
            horizontalContent.removeEventListener('scroll', onTableScroll);
            stickyScrollbar.removeEventListener('scroll', onStickyScroll);
            window.removeEventListener('resize', onResize);
        };
    }, [timesheets]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDidNotWorkDialog = async (timesheet) => {
        setIsLoading(true);
        try {
            const request = {
                page: 1,
                offset: 0,
                sortBy: 'clinicianLastName',
                sortDirection: 'asc',
                limit: 50,
                statuses: [],
                divisions: [],
                specialProcessing: [],
                selectedStartDate: timesheet.weekEnding,
                selectedEndDate: timesheet.weekEnding,
                search: timesheet.bookingName,
                searchKey: { name: 'Booking ID', value: 'bookingName' },
            };
            const response = await getTimesheets(request);
            setDnwTimesheet(timesheet);
            setDnwTimesheetsList(response.data);
            setDidNotWorkDialogOpen(true);
        } catch (error) {
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDidNotWorkConfirm = async (locationTimesheet) => {
        setIsLoading(true);
        try {
            const aTimesheet = await getTimesheetById(
                locationTimesheet.clinicianId,
                locationTimesheet.id
            );

            aTimesheet.status = TimesheetStatusEnumApi.DidNotWork;
            saveTimesheet(aTimesheet, aTimesheet.id).then(() => {
                setIsLoading(false);
                getData();
            });
        } catch (error) {
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
            setIsLoading(false);
        }
    };

    const handleView = (timesheet) => {
        const url = config.getViewUrl(timesheet);
        history.push(url);
    };

    const handleEdit = (timesheet) => {
        const url = config.getEditUrl(timesheet);
        history.push(url);
    };

    const getReviewerNames = () => {
        let reviewerFirstName = '';
        let reviewerLastName = '';
        if (user && user?.family_name && user?.given_name) {
            reviewerFirstName = user.given_name;
            reviewerLastName = user.family_name;
        } else if (user && user.name) {
            const names = user.name.split(' ');
            reviewerFirstName = names.slice(0, -1).join(' ');
            reviewerLastName = names[names.length - 1];
        }
        if (user && user.firstName && user.lastName) reviewerFirstName = user.firstName;
        if (user && user.lastName) reviewerLastName = user.lastName;
        return { reviewerFirstName, reviewerLastName };
    };

    const handleStatusUpdate = async (
        requestedActionEnding,
        { id, reviewId, locationId, name, created, lastModified }
    ) => {
        setIsLoading(true);
        const { reviewerFirstName, reviewerLastName } = getReviewerNames();
        const timesheetReview = {
            id: reviewId,
            timesheetId: id,
            locationId,
            status: requestedActionEnding,
            reviewerFirstName,
            reviewerLastName,
            reviewerEmail: user?.email,
            reviewDate: moment().utc().format(),
            rejectionReason: !rejectionReason || rejectionReason === '' ? null : rejectionReason,
            created,
            lastModified,
        };
        try {
            await updateTimesheetReview(id, reviewId, timesheetReview);
            setAlert({
                message: `${name}'s timesheet has been successfully ${requestedAction.toLowerCase()}!`,
                type: 'success',
            });
            setRequestedAction();
            setIsLoading(false);
            getData();
            setSelectedTimesheetReview(null);
        } catch (error) {
            if (
                error?.errors?.length &&
                error.errors[0].errorCode &&
                ClientTableConfig.errorCodes[error.errors[0].errorCode]
            ) {
                setStatusErrorType(ClientTableConfig.errorCodes[error.errors[0].errorCode]);
                setStatusErrorDialogOpen(true);
            } else {
                setAlert({
                    message: `Timesheet has failed to be ${requestedAction.toLowerCase()}. Please try again.`,
                    type: 'error',
                });
            }
            setSelectedTimesheetReview(null);
            setIsLoading(false);
        }
    };

    const fetchEmailRecipients = async ({ id, reviewId }) => {
        setIsLoading(true);
        try {
            const response = await getEmailRecipients(id, reviewId);
            setEmailsList(response.emailTypes);
            setEmailDialogOpen(true);
        } catch (error) {
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getDownloadTimesheet = async ({ clinicianId, id, locationId }, filename) => {
        setIsLoading(true);
        try {
            const response = await downloadTimesheet(clinicianId, id, locationId, filename);
        } catch (error) {
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const sendManualEmail = async (timesheet, communicationEventType, nameList) => {
        try {
            await sendManualEmailRequest(timesheet.id, timesheet.reviewId, communicationEventType);
            let names;
            if (nameList.length > 1) {
                names = `${nameList[0]} and ${nameList[1]}`;
            } else {
                names = nameList;
            }
            setAlert({
                message: `A reminder has been sent to ${names}!`,
                type: 'success',
            });
        } catch (error) {
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleManualEmail = async (eventTypes, nameList) => {
        setIsLoading(true);
        try {
            const promises = eventTypes?.map(
                async (communicationEventType) =>
                    await sendManualEmail(selectedTimesheetReview, communicationEventType, nameList)
            );
            await Promise.all(promises);
            setIsLoading(false);
        } catch (error) {
            const message = formatErrorMessage(error);
            setAlert({
                message,
                type: 'error',
            });
            setIsLoading(false);
        }
    };

    const renderTimesheets = () => (
        <>
            {(isLoading || isSorting || !user) && <CircularProgress color="primary" />}
            {!isLoading && !isSorting && timesheets.length === 0 && (
                <Typography variant="body2">No results found</Typography>
            )}
            {!isLoading && !isSorting && timesheets.length !== 0 && renderTimesheetListGrid()}
        </>
    );

    const renderTimesheetHeader = () => (
        <Grid container justifyContent="space-between">
            <Grid item>
                <Typography variant="h5" component="h5">
                    Week Ending {moment(weekEnding).format('M/D/YY')}
                </Typography>
            </Grid>
        </Grid>
    );

    const renderTimesheetListHeader = () => (
        <Hidden only={['xs', 'sm']}>
            <Grid container className={classes.headerRow}>
                {config.tableColumns.map(({ name, sortKey, size, sx }) => {
                    const sorted = sortBy === sortKey;
                    return (
                        <Grid item xs={size} key={name} className={classes.headerCell} sx={sx}>
                            <div
                                className={classes.paragraph3}
                                style={{ fontWeight: sorted ? 700 : 'initial' }}
                            >
                                {sortKey ? (
                                    <span onClick={() => handleSort(sortKey)}>{name}</span>
                                ) : (
                                    name
                                )}
                                {sorted && (
                                    <ArrowDropUpIcon
                                        className={`${classes.sortIcon} ${sortDirection}`}
                                        fontSize="small"
                                    />
                                )}
                                {!sorted && sortKey && renderSortableArrow()}
                            </div>
                        </Grid>
                    );
                })}
            </Grid>
        </Hidden>
    );

    const renderTimesheetListGrid = () => (
        <div>{timesheets.map((timesheet) => renderTimesheetListGridRow(timesheet))}</div>
    );

    const renderTimesheetListGridRow = (timesheet) => {
        const uniqueKey = `${timesheet.id}-${timesheet.reviewId}-${timesheet.locationId}`;
        return (
            <Grid container className={classes.tableRow} key={uniqueKey}>
                {config.tableColumns.map(({ key, size, sx }) => {
                    const isApprover = key === 'approver';
                    if (isApprover) {
                        const useActual = completedStatuses.includes(timesheet.status);
                        key = useActual ? 'actualApprover' : 'assignedApprover';
                    }
                    const isEmpty = timesheet[key] === 0 || !timesheet[key];
                    const isStatus = key === 'status';
                    const isName = key === 'name';
                    const isClincianComments = key === 'clinicianComments';
                    const isHasComments = key === 'comments';
                    const isActions = key === 'actions';
                    const isAb5 = key === 'ab5';
                    const isBooked = key === 'isBooked';
                    if (isClincianComments) {
                        const comment = timesheet.clinicianComments || '';
                        const truncatedComment =
                            comment.length > 35 ? `${comment.substring(0, 35)}...` : comment;

                        return (
                            <Grid
                                key={`${uniqueKey}-${key}`}
                                item
                                xs={size}
                                className={classes.commentsCell}
                                onClick={() => handleView(timesheet)}
                            >
                                {comment && (
                                    <ToolTipComponent
                                        disableFocusListener
                                        enterTouchDelay={150}
                                        interactive
                                        placement="right"
                                        title={<Typography variant="string">{comment}</Typography>}
                                    >
                                        <Typography variant="body2">{truncatedComment}</Typography>
                                    </ToolTipComponent>
                                )}
                            </Grid>
                        );
                    }
                    if (isHasComments) {
                        if (!timesheet.comments) return <Grid item xs={size} />;
                        return (
                            <Grid item xs={size} className={classes.commentIcon}>
                                {renderCommentToolTip(timesheet.comments)}{' '}
                            </Grid>
                        );
                    }
                    if (isName) return renderTimesheetName(timesheet, size, uniqueKey, sx);
                    if (isStatus) return renderStatusChip(timesheet, size, uniqueKey, sx);
                    if (isActions) return renderActions(timesheet, size, uniqueKey);
                    if (isAb5) {
                        return (
                            <Grid
                                key={`${uniqueKey}-${key}`}
                                item
                                xs={size}
                                className={classes.tableCell}
                                onClick={() => handleView(timesheet)}
                                sx={sx}
                            >
                                <Typography variant="body2">
                                    {timesheet.aB5 === 'Yes' ? 'W2' : ''}
                                </Typography>
                            </Grid>
                        );
                    }
                    if (isBooked) {
                        return (
                            <Grid
                                key={`${uniqueKey}-${key}`}
                                item
                                xs={size}
                                className={classes.tableCell}
                                onClick={() => handleView(timesheet)}
                                sx={{ display: 'flex', alignItems: 'center', ...sx }}
                            >
                                {timesheet.isBooked === true && (
                                    <ToolTipComponent
                                        disableFocusListener
                                        enterTouchDelay={150}
                                        placement="top"
                                        title="Clinician is booked to work this week."
                                    >
                                        <EventAvailableIcon
                                            fontSize="small"
                                            data-testid="booked-indicator"
                                            aria-label="Clinician is booked to work this week."
                                            sx={{ color: COLORS.LT_JOURNEY }}
                                        />
                                    </ToolTipComponent>
                                )}
                            </Grid>
                        );
                    }
                    return (
                        <Grid
                            key={`${uniqueKey}-${key}`}
                            item
                            xs={size}
                            className={classes.tableCell}
                            onClick={() => handleView(timesheet)}
                            sx={sx}
                        >
                            <Typography
                                variant="body2"
                                title={timesheet[key]}
                                className={isEmpty ? classes.emptyText : null}
                            >
                                {isEmpty ? '-' : timesheet[key]}
                            </Typography>
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    const renderStatusChip = (timesheet, size, uniqueKey, sx) => {
        const statusLabel =
            timesheet.statusForDisplay ||
            TimesheetStatusEnum[timesheet.status] ||
            TimesheetStatusEnum.NotStarted;
        return (
            <Grid
                item
                xs={size}
                className={classes.tableCell}
                sx={sx}
                key={`${uniqueKey}-status`}
                onClick={() => handleView(timesheet)}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <Chip
                    label={statusLabel}
                    className={classes[`chip${timesheet.status}`]}
                    classes={{ root: classes.chipRoot }}
                    title={statusLabel}
                    sx={{ border: '1px solid' }}
                />
            </Grid>
        );
    };

    const renderCommentToolTip = (comments) => {
        if (!comments) return null;
        return (
            <ToolTipComponent
                disableFocusListener
                enterTouchDelay={150}
                interactive
                placement="right"
                title="This timesheet contains one or more comments."
            >
                <ChatBubbleIcon fontSize="small" className={classes.chatButtonIcon} />
            </ToolTipComponent>
        );
    };

    const renderTimesheetName = (timesheet, size, uniqueKey, sx) => (
        <Grid item xs={size} className={classes.tableCell} key={`${uniqueKey}-name`} sx={sx}>
            <Stack
                direction="row"
                alignItems="center"
                onClick={() => handleView(timesheet)}
                gap={1}
            >
                <Typography
                    variant="body2"
                    className={classes.timesheetName}
                    title={timesheet.name}
                >
                    {timesheet.name}
                </Typography>
                {renderCommentToolTip(timesheet.clinicianComments, false)}
            </Stack>
            <Typography variant="body2" className={classes.bookingName}>
                <SalesforceBookingLink
                    bookingSfId={timesheet.bookingSfId}
                    label={timesheet.bookingName}
                    stopPropagation
                />
            </Typography>
        </Grid>
    );

    const renderActions = (timesheet, size, uniqueKey) => (
        <Grid
            item
            xs={size}
            className={classes.actionsCell}
            key={`${uniqueKey}-actions`}
            flexShrink={1}
        >
            <IconButton
                className={classes.menuButton}
                color="default"
                aria-label="More options"
                onClick={handleClick}
                id={`${uniqueKey}-btn`}
                size="large"
            >
                <MoreVert />
            </IconButton>
            <Menu
                id={`${uniqueKey}-menu`}
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl?.id === `${uniqueKey}-btn`)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {config.actions.map(({ name, requestedStatus, icon }) => {
                    const externalAccount =
                        timesheet.locationAccountLevel?.toLowerCase().includes('external') &&
                        timesheet.locationAccountLevel !== 'External (Client Portal - No Time)' &&
                        timesheet.locationAccountLevel !==
                            'External (Client Portal + Scheduler + No Time)';
                    const readOnly =
                        (timesheet.status !== TimesheetStatusEnumApi.Submitted &&
                            (name === 'Reject' || name === 'Approve')) ||
                        (userIsReadOnly && name !== 'View');
                    const hideRecall =
                        ((timesheet.status !== TimesheetStatusEnumApi.Submitted &&
                            timesheet.status !== TimesheetStatusEnumApi.TimeNotEntered) ||
                            timesheet.timesheetStatus === TimesheetStatusEnumApi.Paid ||
                            timesheet.timesheetStatus === TimesheetStatusEnumApi.Approved) &&
                        name === 'Recall';
                    const hideEmailClient =
                        (timesheet.status !== TimesheetStatusEnumApi.Submitted ||
                            externalAccount ||
                            timesheet?.isPaperTimesheet) &&
                        name === 'Email Client';
                    const hideEmailClinician =
                        ((timesheet.status !== TimesheetStatusEnumApi.Rejected &&
                            timesheet.status !== TimesheetStatusEnumApi.PendingSubmission &&
                            timesheet.status !== TimesheetStatusEnumApi.NotStarted &&
                            timesheet.status !== TimesheetStatusEnumApi.Approved) ||
                            externalAccount ||
                            timesheet?.isPaperTimesheet) &&
                        name === 'Email Clinician';
                    const hideDidNotWork =
                        timesheet.status !== TimesheetStatusEnumApi.PendingSubmission &&
                        timesheet.status !== TimesheetStatusEnumApi.NotStarted &&
                        name === 'Did Not Work';
                    const hideEdit =
                        name === 'Edit' &&
                        (!ltoeApiTimesheetEnableEditApprovedTimesheet ||
                            isLTAssociateUser ||
                            (timesheet.status !== TimesheetStatusEnumApi.Approved &&
                                timesheet.status !== TimesheetStatusEnumApi.TimeNotEntered) ||
                            timesheet.processedTimesheetId == null);
                    if (
                        readOnly ||
                        hideEmailClient ||
                        hideEmailClinician ||
                        hideRecall ||
                        hideDidNotWork ||
                        hideEdit
                    )
                        return;
                    return (
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                setRequestedAction(requestedStatus);
                                setSelectedTimesheetReview(timesheet);
                                if (requestedStatus === TimesheetStatusEnumApi.Rejected) {
                                    setRejectionDialogOpen(true);
                                } else if (requestedStatus === 'DidNotWork') {
                                    handleDidNotWorkDialog(timesheet);
                                } else if (requestedStatus === 'View') {
                                    handleView(timesheet);
                                } else if (requestedStatus === 'Edit') {
                                    handleEdit(timesheet);
                                } else if (
                                    requestedStatus === 'EmailClinician' ||
                                    requestedStatus === 'EmailClient'
                                ) {
                                    fetchEmailRecipients(timesheet);
                                } else if (requestedStatus === 'DownloadTimesheet') {
                                    const filename = `${timesheet.name}-${
                                        timesheet.location
                                    } ${moment(timesheet.weekEnding).format('MM-DD-YYYY')}.pdf`;
                                    getDownloadTimesheet(timesheet, filename);
                                } else if (
                                    requestedStatus === TimesheetStatusEnumApi.PendingSubmission
                                ) {
                                    setRecallDialogOpen(true);
                                } else if (requestedStatus === TimesheetStatusEnumApi.Approved) {
                                    setStatusUpdateModalOpen(true);
                                } else {
                                    handleStatusUpdate(requestedStatus, timesheet);
                                }
                            }}
                            key={name}
                        >
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item className={classes.menuIcon}>
                                    {icon}
                                </Grid>
                                <Grid item>
                                    <Typography variant="body2">{name}</Typography>
                                </Grid>
                            </Grid>
                        </MenuItem>
                    );
                })}
            </Menu>
        </Grid>
    );

    const renderSortableArrow = () => (
        <Grid container direction="column" className={classes.sortIcons}>
            <ArrowDropUpIcon className={`${classes.sortIcon} asc`} fontSize="small" />
            <ArrowDropUpIcon className={`${classes.sortIcon} desc`} fontSize="small" />
        </Grid>
    );

    const renderStatusErrorDialog = () => {
        const displayAction = statusErrorType === requestedAction ? 'Updated' : requestedAction;

        const props = {
            name: 'status-error',
            message: `This timesheet has been recently ${statusErrorType} and is not available
								to be ${displayAction}.`,
            onClose: () => {
                setStatusErrorDialogOpen(false);
            },
            onExited: () => {
                setRequestedAction();
                getData();
            },
            open: statusErrorDialogOpen,
        };
        return <StatusErrorDialog {...props} />;
    };

    const renderDidNotWorkDialog = () => {
        const props = {
            name: 'status-didnotwork',
            timesheetsList: dnwTimesheetsList,
            timesheet: dnwTimesheet,
            isLoading,
            onClose: () => {
                setDidNotWorkDialogOpen(false);
            },
            handleAction: (aLocationTimesheet) => {
                handleDidNotWorkConfirm(aLocationTimesheet);
                setDidNotWorkDialogOpen(false);
            },
            open: didNotWorkDialogOpen,
        };
        return <DidNotWorkDialog {...props} />;
    };

    const renderEmailDialog = () => {
        const props = {
            name: 'status-email',
            emailsList,
            isLoading,
            onClose: () => {
                setEmailDialogOpen(false);
            },
            handleAction: (eventTypes, nameList) => {
                handleManualEmail(eventTypes, nameList);
                setEmailDialogOpen(false);
            },
            open: emailDialogOpen,
        };
        return <EmailDialog {...props} />;
    };

    const renderRejectionDialog = () => {
        const props = {
            name: 'status-error',
            message: '*Selecting cancel will NOT reject the timesheet',
            title: 'Reason for rejecting',
            onClose: () => {
                setRejectionDialogOpen(false);
            },
            handleAction: () => {
                handleStatusUpdate(TimesheetStatusEnumApi.Rejected, selectedTimesheetReview);
                setRejectionDialogOpen(false);
            },
            open: rejectionDialogOpen,
            setRejectionReason: (value) => {
                setRejectionReason(value);
            },
        };
        return <RejectionDialog {...props} />;
    };

    const renderStatusUpdateConfirmationDialog = () => {
        const locationName = selectedTimesheetReview?.location;
        const props = {
            name: 'status-update-confirmation',
            message: `Are you sure you would like to mark ${moment(
                selectedTimesheetReview?.weekEnding
            ).format('MMMM Do')} at ${locationName} as ${requestedAction?.toLowerCase()}?`,
            open: statusUpdateModalOpen,
            onClose: () => {
                setStatusUpdateModalOpen(false);
                setIsLoading(false);
            },
            handleAction: () => {
                try {
                    setStatusUpdateModalOpen(false);
                    setIsLoading(true);
                    handleStatusUpdate(requestedAction, selectedTimesheetReview);
                } catch (error) {
                    setAlert({
                        message: `Timesheet has failed to be ${selectedTimesheetReview.toLowerCase()}. Please try again.`,
                        type: 'error',
                    });
                }
            },
            primaryActionName: 'Confirm',
            secondaryActionName: 'Cancel',
        };
        return <StatusWarningDialog {...props} />;
    };

    const renderRecallDialog = () => {
        const props = {
            name: 'recall-warning',
            message:
                'Recalling a timesheet will recall all submitted locations, stop the approval process, and will require the recalled timesheets to be re-submitted.',
            onClose: () => {
                setRecallDialogOpen(false);
            },
            handleAction: async () => {
                try {
                    setRecallDialogOpen(false);
                    setIsLoading(true);
                    const timesheet = await getTimesheetById(
                        selectedTimesheetReview?.clinicianId,
                        selectedTimesheetReview?.id
                    );
                    setRecallErrorType(timesheet.status.toLowerCase());
                    const currentTimsheetHasEntryInRecallableStatus =
                        timesheet?.timesheetReviews?.some(
                            (review) =>
                                review.status === TimesheetStatusEnumApi.Submitted ||
                                review.status === TimesheetStatusEnumApi.TimeNotEntered
                        );
                    if (!currentTimsheetHasEntryInRecallableStatus) {
                        setRecallErrorDialogOpen(true);
                        setIsLoading(false);
                    } else {
                        timesheet.status = TimesheetStatusEnumApi.PendingSubmission;
                        await saveTimesheet(timesheet, selectedTimesheetReview?.id, true);
                        selectedTimesheetReview.status = TimesheetStatusEnumApi.PendingSubmission;
                        handleView(selectedTimesheetReview);
                    }
                } catch (error) {
                    setIsLoading(false);
                    console.error(error);
                }
            },
            open: recallDialogOpen,
            primaryActionName: 'Recall',
            secondaryActionName: 'Cancel',
        };
        return <StatusWarningDialog {...props} />;
    };

    const renderRecallErrorDialog = () => {
        const props = {
            name: 'recall-error',
            message: `This timesheet has been recently ${recallErrorType} and is not available
								to be recalled. Please reach out to your recruiter with any
								questions.`,
            onClose: () => {
                setRecallErrorDialogOpen(false);
                setIsLoading(false);
            },
            onExited: () => {
                setRequestedAction();
                history.go(0);
            },
            open: recallErrorDialogOpen,
        };
        return <StatusErrorDialog {...props} />;
    };

    return (
        <Paper classes={{ root: classes.PaperRoot }}>
            {renderTimesheetHeader()}
            <Box>
                <div className={classes.tableScrollContainer} ref={tableScrollRef}>
                    <div className={classes.tableContent}>
                        {renderTimesheetListHeader()}
                        {renderTimesheets()}
                    </div>
                </div>
                <div className={classes.stickyHorizontalScrollbar} ref={stickyScrollbarRef}>
                    <Box sx={{ height: '1px', width: stickyScrollWidth }} />
                </div>
            </Box>
            {renderStatusErrorDialog()}
            {renderRejectionDialog()}
            {renderEmailDialog()}
            {renderDidNotWorkDialog()}
            {renderRecallDialog()}
            {renderRecallErrorDialog()}
            {renderStatusUpdateConfirmationDialog()}
        </Paper>
    );
};

export default withTranslation()(withStyles(styles)(TimesheetReviewCard));
